import type { CarSnapshot, SimEvent } from '../sim/types';

/**
 * Audio 100 % procedural (sin assets): motor ligado a las rpm, ruido de
 * rodadura, tic-tac del intermitente, zumbido del ABS, claxon y golpes.
 */
export class EngineAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private oscA: OscillatorNode | null = null;
  private oscB: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private noiseSrc: AudioBufferSourceNode | null = null;
  private noiseGain: GainNode | null = null;
  private hornOsc: OscillatorNode | null = null;
  private hornGain: GainNode | null = null;
  private enabled: boolean;
  private isEv: boolean;
  private redlineRpm: number;
  private blinkerOn = false;
  private lastBlinkTick = 0;

  constructor(enabled: boolean, isEv: boolean, redlineRpm = 6300) {
    this.enabled = enabled;
    this.isEv = isEv;
    this.redlineRpm = redlineRpm;
  }

  /** Debe llamarse tras un gesto del usuario (política de autoplay). */
  private ensureContext(): void {
    if (!this.enabled || this.ctx) return;
    try {
      const ctx = new AudioContext();
      this.ctx = ctx;
      this.master = ctx.createGain();
      this.master.gain.value = 0.5;
      this.master.connect(ctx.destination);

      // Motor: dos osciladores desafinados
      this.engineGain = ctx.createGain();
      this.engineGain.gain.value = 0;
      this.engineGain.connect(this.master);
      this.oscA = ctx.createOscillator();
      this.oscA.type = this.isEv ? 'sine' : 'sawtooth';
      this.oscB = ctx.createOscillator();
      this.oscB.type = this.isEv ? 'sine' : 'square';
      const bGain = ctx.createGain();
      bGain.gain.value = this.isEv ? 0.3 : 0.45;
      this.oscA.connect(this.engineGain);
      this.oscB.connect(bGain).connect(this.engineGain);
      this.oscA.start();
      this.oscB.start();

      // Ruido de rodadura
      const noiseLen = ctx.sampleRate;
      const buffer = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      let seed = 22222;
      for (let i = 0; i < noiseLen; i++) {
        seed = (seed * 1664525 + 1013904223) >>> 0;
        data[i] = (seed / 0xffffffff) * 2 - 1;
      }
      this.noiseSrc = ctx.createBufferSource();
      this.noiseSrc.buffer = buffer;
      this.noiseSrc.loop = true;
      const noiseFilter = ctx.createBiquadFilter();
      noiseFilter.type = 'lowpass';
      noiseFilter.frequency.value = 600;
      this.noiseGain = ctx.createGain();
      this.noiseGain.gain.value = 0;
      this.noiseSrc.connect(noiseFilter).connect(this.noiseGain).connect(this.master);
      this.noiseSrc.start();

      // Claxon
      this.hornOsc = ctx.createOscillator();
      this.hornOsc.type = 'square';
      this.hornOsc.frequency.value = 420;
      this.hornGain = ctx.createGain();
      this.hornGain.gain.value = 0;
      this.hornOsc.connect(this.hornGain).connect(this.master);
      this.hornOsc.start();
    } catch {
      this.ctx = null;
    }
  }

  /** Registra los listeners de primer gesto sobre window. */
  attachGestureUnlock(): void {
    if (!this.enabled) return;
    const unlock = (): void => {
      this.ensureContext();
      void this.ctx?.resume();
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);
  }

  update(snap: CarSnapshot, events: SimEvent[]): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;

    const man = snap.manual;
    if (this.isEv) {
      const whine = 90 + snap.speedKmh * 9;
      this.oscA?.frequency.setTargetAtTime(whine, t, 0.05);
      this.oscB?.frequency.setTargetAtTime(whine * 1.98, t, 0.05);
      const g = Math.min(0.06, 0.008 + snap.speedKmh * 0.0009 + snap.throttle * 0.02);
      this.engineGain?.gain.setTargetAtTime(snap.gear === 'P' ? 0 : g, t, 0.08);
    } else if (man && !man.engineOn) {
      // Motor calado o arrancando: silencio, o el "ra-ra-ra" grave del starter
      if (man.cranking) {
        this.oscA?.frequency.setTargetAtTime(34, t, 0.02);
        this.oscB?.frequency.setTargetAtTime(52, t, 0.02);
        this.engineGain?.gain.setTargetAtTime(0.05, t, 0.03);
      } else {
        this.engineGain?.gain.setTargetAtTime(0, t, 0.05);
      }
    } else {
      const rel = snap.rpm / this.redlineRpm;
      const f = 46 + rel * 150;
      this.oscA?.frequency.setTargetAtTime(f, t, 0.04);
      this.oscB?.frequency.setTargetAtTime(f * 1.5 + 3, t, 0.04);
      let g = 0.035 + snap.throttle * 0.075 + rel * 0.05;
      // El patinado del embrague se oye: el motor "se escapa" del coche
      if (man && Math.abs(man.slipRpm) > 400 && man.engagement > 0.1) g += 0.02;
      this.engineGain?.gain.setTargetAtTime(snap.shifting ? g * 0.5 : g, t, 0.06);
    }

    // Rodadura + zumbido ABS
    const roll = Math.min(0.1, snap.speedKmh * 0.0016);
    const abs = snap.absActive ? 0.1 : 0;
    this.noiseGain?.gain.setTargetAtTime(roll + abs, t, 0.05);

    // Claxon
    this.hornGain?.gain.setTargetAtTime(snap.horn ? 0.12 : 0, t, 0.01);

    // Tic-tac del intermitente (cada ~0,4 s)
    const anySignal = snap.signalLeft || snap.signalRight;
    if (anySignal && snap.tick - this.lastBlinkTick >= 24) {
      this.lastBlinkTick = snap.tick;
      this.blinkerOn = !this.blinkerOn;
      this.click(this.blinkerOn ? 1150 : 900, 0.05);
    }

    for (const ev of events) {
      if (ev.type === 'collision') this.thud(Math.min(1, ev.speedKmh / 40));
      if (ev.type === 'gearDenied') this.click(220, 0.12);
      if (ev.type === 'gearChanged') this.click(500, 0.05);
      if (ev.type === 'stall') {
        this.thud(0.5);
        this.click(160, 0.1);
      }
      if (ev.type === 'engineStart') this.click(640, 0.07);
      if (ev.type === 'gearGrind') this.grind();
      if (ev.type === 'overRev') this.click(1400, 0.09);
    }
  }

  /** Rascada de marcha: ráfaga corta de "rrrrk". */
  private grind(): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const t = ctx.currentTime;
    for (let i = 0; i < 4; i++) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = 330 - i * 40;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.09, t + i * 0.045);
      g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.045 + 0.05);
      osc.connect(g).connect(this.master);
      osc.start(t + i * 0.045);
      osc.stop(t + i * 0.045 + 0.07);
    }
  }

  private click(freq: number, gain: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.08);
  }

  private thud(strength: number): void {
    const ctx = this.ctx;
    if (!ctx || !this.master) return;
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 70;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(0.25 * strength + 0.05, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.25);
    osc.connect(g).connect(this.master);
    osc.start(t);
    osc.stop(t + 0.3);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (this.master) this.master.gain.value = enabled ? 0.5 : 0;
  }

  dispose(): void {
    try {
      this.oscA?.stop();
      this.oscB?.stop();
      this.noiseSrc?.stop();
      this.hornOsc?.stop();
      void this.ctx?.close();
    } catch {
      /* ya cerrado */
    }
    this.ctx = null;
  }
}
